import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { LoadingModal } from "../components/common/LoadingModal";
import { registerLoader } from "../api/axiosClient";

export interface ShowLoaderOptions {
  /**
   * Wait this long before covering the screen, and skip it altogether if
   * everything pending finishes first. For reads (searches, lookups): the
   * page has its own loading state, and a full-screen overlay flashing up
   * for a 100 ms search made every page feel slower than it was.
   */
  delayMs?: number;
}

interface LoadingContextType {
  show: (text?: string, options?: ShowLoaderOptions) => void;
  hide: () => void;
  setText: (text: string) => void;
}

const LoadingContext = createContext<LoadingContextType | null>(null);

// Every axios request now calls show()/hide() automatically (see
// axiosClient.ts's interceptors) alongside the existing manual show()/
// hide() call sites scattered through the app — so several calls can
// legitimately overlap (e.g. a page's own show() around a multi-request
// action, plus each of those requests' own auto show()/hide()). A plain
// boolean would let the first one to finish hide the modal out from under
// the others still in flight; a pending-count only closes it once every
// caller has called hide().
export function LoadingProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("Loading...");
  const pendingCount = useRef(0);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // show/hide touch only refs and state setters, so they are created once:
  // a new pair (and a new context value) on every open/close re-rendered
  // every page that calls useLoading, and broke their memos.
  const clearOpenTimer = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  const show = useCallback(
    (msg?: string, options?: ShowLoaderOptions) => {
      if (msg) setText(msg);
      pendingCount.current += 1;
      const delay = options?.delayMs ?? 0;
      if (delay <= 0) {
        // An immediate show (a save, a manual call) wins over any
        // delayed one still waiting.
        clearOpenTimer();
        setOpen(true);
        return;
      }
      if (openTimer.current) return;
      openTimer.current = setTimeout(() => {
        openTimer.current = null;
        if (pendingCount.current > 0) setOpen(true);
      }, delay);
    },
    [clearOpenTimer],
  );

  const hide = useCallback(() => {
    pendingCount.current = Math.max(0, pendingCount.current - 1);
    if (pendingCount.current === 0) {
      clearOpenTimer();
      setOpen(false);
      setText("Loading...");
    }
  }, [clearOpenTimer]);

  useEffect(() => {
    registerLoader({ show, hide });
  }, [show, hide]);

  const value = useMemo(() => ({ show, hide, setText }), [show, hide]);

  return (
    <LoadingContext.Provider value={value}>
      {children}

      {/* GLOBAL MODAL */}
      <LoadingModal open={open} text={text} />
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error("useLoading must be used inside LoadingProvider");
  return ctx;
}
